import { useEffect, useRef } from 'react';
import { User } from '../types';
import client from '../clients/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useRecoilState } from 'recoil';
import userState from '../atoms/user-state';
import { authStatusState, authErrorState } from '../atoms/auth-state';

interface UseAuthorizationOptions {
	canGetToken: boolean;
	redirectOnUnauthorized?: string;
	redirectOnError?: string;
	autoLogoutOnUnauthorized?: boolean;
}

const useAuthorization = ({
	canGetToken,
	redirectOnUnauthorized = '/access-error',
	redirectOnError = '/auth-error',
	autoLogoutOnUnauthorized = false
}: UseAuthorizationOptions): { user: User | null; status: string; error: string | null } => {
	const [theUser, setTheUser] = useRecoilState(userState);
	const [status, setStatus] = useRecoilState(authStatusState);
	const [error, setError] = useRecoilState(authErrorState);
	const { getAccessTokenSilently, logout } = useAuth0();
	const hasAttemptedFetch = useRef(false);

	// Rehidratación inicial (solo primera vez)
	useEffect(() => {
		if (!theUser?.token && status === 'idle') {
			const savedUser = localStorage.getItem('user');
			if (savedUser) {
				try {
					const parsed = JSON.parse(savedUser);
					if (parsed?.error === false && parsed?.user?.token) {
						const savedUserLowerCasedEmail = (parsed.user?.email)?.toLowerCase();
						setTheUser({ ...parsed.user, email: savedUserLowerCasedEmail });
						setStatus('ready');
					}
				} catch {
					// Ignorar errores de parseo
				}
			}
		}
	}, []); // Solo al montar

	useEffect(() => {
		let cancelled = false;

		const run = async () => {
			// Evitar múltiples ejecuciones y verificar condiciones de salida
			if (!canGetToken || theUser?.token || hasAttemptedFetch.current) {
				return;
			}

			hasAttemptedFetch.current = true;
			setStatus('loading');
			setError(null);

			try {
				const tokk = await getAccessTokenSilently();
				const res = await client.silentMe({ token: tokk });

				if (cancelled) return; // Chequear antes de procesar

				if (res.data.error === true) {
					const notProvisioned = res.data.message === 'User not found' || res.status === 404;
					if (notProvisioned) {
						setStatus('unauthorized');
						setError('Usuario no provisionado en la base de datos.');
						if (autoLogoutOnUnauthorized) {
							logout({ logoutParams: { returnTo: window.location.origin + redirectOnUnauthorized } });
						}

						return;
					}

					setStatus('error');
					setError('Error obteniendo datos de usuario.');
					return;
				}

				const finalUser = { ...res.data.user, email: res.data?.user?.email?.toLowerCase() };
				const finalToken = res.data.token;

				localStorage.setItem('user', JSON.stringify({ user: { ...finalUser, token: finalToken }, error: false }));
				localStorage.setItem('token', JSON.stringify(finalToken));
				setTheUser({ ...finalUser, token: finalToken });
				setStatus('ready');
			} catch (e: any) {
				if (cancelled) return;

				const unauthorized = e?.error === 'login_required' || e?.status === 401;
				if (unauthorized) {
					setStatus('unauthorized');
					setError('Sesión no válida.');
					if (autoLogoutOnUnauthorized) {
						logout({ logoutParams: { returnTo: window.location.origin + redirectOnUnauthorized } });
					}
				} else {
					setStatus('error');
					setError('Fallo técnico de autenticación.');
				}
			}
		};

		// Resetear el flag cuando canGetToken cambia de false a true
		if (canGetToken && !theUser?.token) {
			hasAttemptedFetch.current = false;
		}

		run();

		return () => {
			cancelled = true;
		};
	}, [canGetToken, theUser?.token, autoLogoutOnUnauthorized, redirectOnUnauthorized]);

	return { user: theUser, status, error };
};

export default useAuthorization;
